// Danmaku (bullet-comment) settings: master on/off, opacity, vertical display area,
// scroll speed and font size. Persisted to localStorage so a viewer's preference sticks
// across streams. The engine (DanmakuLayer) reads these to size lanes and pace bullets.
import { useCallback, useState } from "react";

export type DanmakuArea = "quarter" | "half" | "full";
export type DanmakuSpeed = "slow" | "normal" | "fast";

export interface DanmakuSettings {
  enabled: boolean; // overlay master switch
  opacity: number; // 0.2 .. 1
  area: DanmakuArea; // fraction of the video height bullets may occupy (from the top)
  speed: DanmakuSpeed; // time a bullet takes to cross the screen
  fontScale: number; // 0.8 (small) | 1 (normal) | 1.25 (large)
}

export const DEFAULT_DANMAKU: DanmakuSettings = {
  enabled: true,
  opacity: 1,
  area: "half",
  speed: "normal",
  fontScale: 1,
};

// Seconds a bullet takes to travel fully across the container (constant per speed, so
// longer comments simply move faster — the classic "all clear in the same time" feel).
export const SPEED_SECONDS: Record<DanmakuSpeed, number> = {
  slow: 14,
  normal: 9,
  fast: 6,
};

export const AREA_FRACTION: Record<DanmakuArea, number> = {
  quarter: 0.25,
  half: 0.5,
  full: 1,
};

const STORAGE_KEY = "maitv.danmaku";

function load(): DanmakuSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_DANMAKU;
    const parsed = JSON.parse(raw) as Partial<DanmakuSettings>;
    return { ...DEFAULT_DANMAKU, ...parsed };
  } catch {
    return DEFAULT_DANMAKU;
  }
}

/** Stateful danmaku settings with a partial updater; every change is persisted. */
export function useDanmakuSettings(): [DanmakuSettings, (patch: Partial<DanmakuSettings>) => void] {
  const [settings, setSettings] = useState<DanmakuSettings>(load);
  const update = useCallback((patch: Partial<DanmakuSettings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore quota / private-mode failures */
      }
      return next;
    });
  }, []);
  return [settings, update];
}
